import React, { useEffect, useState } from "react";
import { FiSave } from "react-icons/fi";
import PageHeaderSetting from "../shared/pageHeader/PageHeaderSetting";
import PerfectScrollbar from "react-perfect-scrollbar";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MASK_VALUE = "********";

const SettingApiForm = () => {
  const [apiSettings, setApiSettings] = useState({
    mailjet_title: "",
    mailjet_secret: "",
    mailjet_api: "",
    s3_cdn_key: "",
    s3_cdn_secret: "",
    s3_cdn_endpoint: "",
    s3_cdn_bucket: "",
    s3_cdn_region: "",
    s3_cdn_path: "",
    paytr_merchant_id: "",
    paytr_merchant_key: "",
    paytr_merchant_salt: "",
    netgsm_username: "",
    netgsm_password: "",
    invoice_title: "",
    invoice_address: "",
    invoice_phone: "",
    iban: "",
    iban_fullname: "",
  });

  const [phoneCountry, setPhoneCountry] = useState("tr");

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

  useEffect(() => {
    fetchApiData();
  }, []);

  // Telefon numarasından ülke kodunu algıla
  const detectCountryFromPhone = (phone) => {
    if (!phone) return "tr";

    const cleaned = phone.replace(/\D/g, "");

    // Yaygın ülke kodları
    if (cleaned.startsWith("90")) return "tr";
    if (cleaned.startsWith("1")) return "us";
    if (cleaned.startsWith("44")) return "gb";
    if (cleaned.startsWith("49")) return "de";
    if (cleaned.startsWith("33")) return "fr";
    if (cleaned.startsWith("39")) return "it";
    if (cleaned.startsWith("34")) return "es";
    if (cleaned.startsWith("31")) return "nl";
    if (cleaned.startsWith("32")) return "be";
    if (cleaned.startsWith("41")) return "ch";
    if (cleaned.startsWith("43")) return "at";
    if (cleaned.startsWith("45")) return "dk";
    if (cleaned.startsWith("46")) return "se";
    if (cleaned.startsWith("47")) return "no";
    if (cleaned.startsWith("358")) return "fi";
    if (cleaned.startsWith("48")) return "pl";
    if (cleaned.startsWith("351")) return "pt";
    if (cleaned.startsWith("30")) return "gr";
    if (cleaned.startsWith("7")) return "ru";
    if (cleaned.startsWith("86")) return "cn";
    if (cleaned.startsWith("81")) return "jp";
    if (cleaned.startsWith("82")) return "kr";
    if (cleaned.startsWith("91")) return "in";
    if (cleaned.startsWith("61")) return "au";
    if (cleaned.startsWith("64")) return "nz";
    if (cleaned.startsWith("27")) return "za";
    if (cleaned.startsWith("55")) return "br";
    if (cleaned.startsWith("52")) return "mx";
    if (cleaned.startsWith("54")) return "ar";
    if (cleaned.startsWith("56")) return "cl";
    if (cleaned.startsWith("57")) return "co";
    if (cleaned.startsWith("971")) return "ae";
    if (cleaned.startsWith("966")) return "sa";
    if (cleaned.startsWith("20")) return "eg";

    return "tr"; // varsayılan
  };

  // Telefon numarasını normalize et (sadece rakamlar)
  const normalizePhoneNumber = (phone) => {
    if (!phone) return "";
    let cleaned = phone.replace(/\D/g, "");
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.substring(1);
    }
    return cleaned;
  };

  const fetchApiData = async () => {
    try {
      const resp = await axios.get(`${BASE_URL}/api/settings/1`);
      const data = resp.data || {};

      // Hassas alanları maskele
      if (data.mailjet_secret && data.mailjet_secret !== "") {
        data.mailjet_secret = MASK_VALUE;
      }
      if (data.s3_cdn_secret && data.s3_cdn_secret !== "") {
        data.s3_cdn_secret = MASK_VALUE;
      }
      if (data.paytr_merchant_key && data.paytr_merchant_key !== "") {
        data.paytr_merchant_key = MASK_VALUE;
      }
      if (data.paytr_merchant_salt && data.paytr_merchant_salt !== "") {
        data.paytr_merchant_salt = MASK_VALUE;
      }
      if (data.netgsm_password && data.netgsm_password !== "") {
        data.netgsm_password = MASK_VALUE;
      }

      setApiSettings((prevState) => ({
        ...prevState,
        ...data,
      }));

      // 🔥 Telefon numarasından ülke kodunu algıla
      if (data.invoice_phone) {
        const detectedCountry = detectCountryFromPhone(data.invoice_phone);
        setPhoneCountry(detectedCountry);
      }
    } catch (err) {
      console.error("API verisi çekme hatası:", err);
      toast.error("API ayarları alınamadı!");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setApiSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSecretFocus = (e) => {
    if (e.target.value === MASK_VALUE) {
      e.target.value = "";
      setApiSettings((prev) => ({
        ...prev,
        [e.target.name]: "",
      }));
    }
  };

  const handleSave = async () => {
    try {
      const dataToSave = { ...apiSettings };

      // Hassas alanları kontrol et
      if (dataToSave.mailjet_secret === MASK_VALUE) {
        delete dataToSave.mailjet_secret;
      }
      if (dataToSave.s3_cdn_secret === MASK_VALUE) {
        delete dataToSave.s3_cdn_secret;
      }
      if (dataToSave.paytr_merchant_key === MASK_VALUE) {
        delete dataToSave.paytr_merchant_key;
      }
      if (dataToSave.paytr_merchant_salt === MASK_VALUE) {
        delete dataToSave.paytr_merchant_salt;
      }
      if (dataToSave.netgsm_password === MASK_VALUE) {
        delete dataToSave.netgsm_password;
      }

      // 🔥 Telefon numarasını normalize et
      if (dataToSave.invoice_phone) {
        dataToSave.invoice_phone = normalizePhoneNumber(
          dataToSave.invoice_phone,
        );
      }

      await axios.put(`${BASE_URL}/api/settings/1`, dataToSave);
      toast.success(getText("SUCCESS_API_UPDATED"));
      fetchApiData();
    } catch (error) {
      console.error("API Bilgileri kaydedilemedi:", error);
      toast.error("Kayıt hatası oluştu!");
    }
  };

  return (
    <div className="content-area setting-form">
      <ToastContainer />
      <PerfectScrollbar>
        <PageHeaderSetting />
        <div className="content-area-body">
          <div className="card mb-0">
            <div className="card-body">
              {/* Fatura Ünvanı */}
              <div className="mb-3">
                <label className="form-label fw-bold">
                  {getText("LABEL_INVOICE_TITLE")}
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="invoice_title"
                  value={apiSettings.invoice_title || ""}
                  onChange={handleChange}
                  placeholder="ABC Teknoloji A.Ş."
                />
              </div>

              {/* Fatura Telefonu - PhoneInput */}
              <div className="mb-3">
                <label className="form-label fw-bold">
                  {getText("LABEL_INVOICE_PHONE")}
                </label>
                <PhoneInput
                  country={phoneCountry}
                  value={apiSettings.invoice_phone || ""}
                  onChange={(value, country) => {
                    setApiSettings((prev) => ({
                      ...prev,
                      invoice_phone: value,
                    }));
                    // Ülke değiştiğinde güncelle
                    if (country?.countryCode) {
                      setPhoneCountry(country.countryCode);
                    }
                  }}
                  enableSearch
                  enableAreaCodes
                  countryCodeEditable={false}
                  inputStyle={{
                    width: "100%",
                    height: "48px",
                    fontSize: "15px",
                    borderRadius: "10px",
                    border: "1px solid #e5e7eb",
                    paddingLeft: "52px",
                  }}
                  buttonStyle={{
                    borderRadius: "10px 0 0 10px",
                    border: "1px solid #e5e7eb",
                    background: "#f8f9fa",
                  }}
                />
              </div>

              {/* Fatura Adresi */}
              <div className="mb-3">
                <label className="form-label fw-bold">
                  {getText("LABEL_INVOICE_ADDRESS")}
                </label>
                <textarea
                  className="form-control"
                  name="invoice_address"
                  rows={3}
                  value={apiSettings.invoice_address || ""}
                  onChange={handleChange}
                  placeholder="İl / İlçe / Açık adres"
                />
              </div>
              {/* IBAN */}
              <div className="mb-3">
                <label className="form-label fw-bold">
                  {getText("LABEL_IBAN")}
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="iban"
                  value={apiSettings.iban || ""}
                  onChange={handleChange}
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                />
              </div>

              {/* IBAN AD SOYAD */}
              <div className="mb-3">
                <label className="form-label fw-bold">
                  {getText("LABEL_IBAN_FULLNAME")}
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="iban_fullname"
                  value={apiSettings.iban_fullname || ""}
                  onChange={handleChange}
                  placeholder="Ad Soyad"
                />
              </div>

              {/* KAYDET BUTONU */}
              <div className="d-flex justify-content-center">
                <button className="btn btn-primary" onClick={handleSave}>
                  <FiSave className="me-2" /> {getText("SAVE_BTN")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </PerfectScrollbar>
    </div>
  );
};

export default SettingApiForm;
